import React, { useState, useContext } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { COVERAGE_FORM } from "@platform/rfp-core";
import { TextField, Typography } from "@platform/design-system";

import {
  BenchmarkSection,
  CommissionSection,
  SectionTitle,
} from "../../components";
import { TabPanel } from "../../../../components";
import { RFP_ACTION_TYPES } from "../../constants";
import { handleNumericFieldChange, isDisabledNextStepFn } from "../utils";
import { RfpContext } from "../../contexts/rfpContext";
import { scrollToTop } from "../../../../utils";
import CoverageButtonWrapper from "../components/coverageButtonWrapper";
import envVariables from "../../../../projectEnvVariables";
import CommentsField from "../components/commentsField";

import { createValidationHandlers } from "./coveragesValidations";
// TODO(eslint): fix cycle
// eslint-disable-next-line import/no-cycle
import { isValidPlan } from "./coverages";

const {
  PLAN_TYPE,
  PLANS,
  ALTERNATE_PLANS,
  ADDITIONAL_NOTES,
  CONTRIBUTION_TYPE,
  CONTRIBUTION_VALUE,
  CONTRIBUTION_STRUCTURE,
  CURRENT_COMMISSION,
  ENROLLED_LIVES,
  REQUESTED_COMMISSION,
  SINGLE_PLAN,
  BASE_BUY_UP,
  TOTAL_EMPLOYER_PAID,
  TOTAL_EMPLOYEE_PAID,
  CONTRIBUTORY,
  EMPLOYER_CONTRIBUTION_PERCENTAGE,
  EMPLOYER_CONTRIBUTION_AMOUNT,
  GROSS_UP,
  BASE_BUY_PLAN_1,
  BASE_BUY_PLAN_2,
  RATE_TYPE,
  MONTHLY_PAYROLL,
  MONTHLY_BENEFITS,
} = COVERAGE_FORM;

const { UPDATE_SELECTED_COVERAGE } = RFP_ACTION_TYPES;

const initialValues = {
  [CONTRIBUTION_STRUCTURE]: "",
  [CONTRIBUTION_TYPE]: "",
  [CONTRIBUTION_VALUE]: "",
  [ENROLLED_LIVES]: "",
  [RATE_TYPE]: "",
  [CURRENT_COMMISSION]: "",
  [REQUESTED_COMMISSION]: "",
  [ADDITIONAL_NOTES]: "",
  [ALTERNATE_PLANS]: "",
};

export const initialLTDCoveragesDetails = {
  [PLAN_TYPE]: SINGLE_PLAN,
  [PLANS]: [
    {
      ...initialValues,
      name: SINGLE_PLAN,
    },
  ],
};

const LongTermDisability = ({
  coverage,
  nextCoverage,
  isLastCoverage,
  handleNext,
  handleBack,
  prevCoverage,
  isFirstCoverage,
}) => {
  const { setRfp } = useContext(RfpContext);
  const [activeTab, setActiveTab] = useState(0);

  const handleFormChange = (updatedCoverage, currentPlanKey, field) => {
    let fieldKey = field;
    if (fieldKey === PLAN_TYPE) {
      setActiveTab(0);
      fieldKey = undefined;
    }
    setRfp({
      type: UPDATE_SELECTED_COVERAGE,
      value: updatedCoverage,
      currentPlanKey,
      fieldKey,
    });
  };

  const handleBackAction = () => {
    if (coverage.details[PLAN_TYPE] === SINGLE_PLAN || activeTab === 0) {
      handleBack();
    } else {
      setActiveTab(0);
    }
    scrollToTop();
  };

  const handleNextAction = () => {
    if (coverage.details[PLAN_TYPE] === SINGLE_PLAN || activeTab === 1) {
      handleNext();
    } else {
      setActiveTab(1);
    }
    scrollToTop();
  };

  const backButtonName =
    coverage.details[PLAN_TYPE] === SINGLE_PLAN || activeTab === 0
      ? prevCoverage?.name
      : BASE_BUY_PLAN_1;

  const nextButtonName =
    coverage.details[PLAN_TYPE] === SINGLE_PLAN || activeTab === 1
      ? nextCoverage?.name
      : BASE_BUY_PLAN_2;

  const displayNextButton = isLastCoverage
    ? coverage.details[PLAN_TYPE] === BASE_BUY_UP && activeTab === 0
    : true;
  const displayBackButton = isFirstCoverage
    ? coverage.details[PLAN_TYPE] === BASE_BUY_UP && activeTab === 1
    : true;

  const isDisabledNextStep = isDisabledNextStepFn(
    coverage,
    SINGLE_PLAN,
    activeTab,
  );

  return (
    <>
      <LongTermDisabilityForm
        coverage={coverage}
        onFormChange={handleFormChange}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
      />
      <CoverageButtonWrapper
        displayBackButton={displayBackButton}
        displayNextButton={displayNextButton}
        backButtonName={backButtonName}
        nextButtonName={nextButtonName}
        handleBackAction={handleBackAction}
        handleNextAction={handleNextAction}
        isDisabled={isDisabledNextStep}
        isBackDisabled={false}
      />
    </>
  );
};

export const LongTermDisabilityForm = ({
  coverage,
  onFormChange,
  activeTab = 0,
  onChangeTab,
}) => {
  const isCurrentCoverage = coverage?.isCurrentCoverage;
  const currentPlan = coverage?.details[PLANS][activeTab];
  const currentPlanErrors = currentPlan?.errors || {};

  const handleChangeTab = (event, newValue) => {
    if (onChangeTab) {
      onChangeTab(newValue);
    }
  };

  const handlePlanTypeChange = (e) => {
    const { value, name } = e.target;
    const plans =
      value === SINGLE_PLAN
        ? [{ ...initialValues, name: SINGLE_PLAN }]
        : [
            { ...initialValues, name: BASE_BUY_PLAN_1 },
            { ...initialValues, name: BASE_BUY_PLAN_2 },
          ];

    const payload = {
      ...coverage,
      details: { ...coverage.details, [name]: value, [PLANS]: plans },
    };

    onFormChange(payload, activeTab, name);
  };

  const handlePlanFormChange = ({ name, value }, planName) => {
    const plans = coverage.details.plans.map((plan) => {
      if (plan.name === planName) {
        // reset contribution value field if contribution type is changed
        if (name === CONTRIBUTION_TYPE) {
          plan[CONTRIBUTION_VALUE] = "";
        }

        return {
          ...plan,
          [name]: value,
        };
      }
      return plan;
    });

    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: plans },
    };

    onFormChange(payload, activeTab, name);
  };

  const { handlePlanFormChangeWithValidation, handlePreviousFieldsValidation } =
    createValidationHandlers({
      currentPlan,
      coverage,
      onFormChange,
      isCurrentCoverage,
      activeTab,
      planName: currentPlan?.name,
    });

  const isTabError = (tab) =>
    !isValidPlan(coverage?.details[PLANS][tab]?.errors);

  const planForm = (planName) => (
    <>
      {isCurrentCoverage && (
        <>
          <SectionTitle>Enrolled Lives</SectionTitle>
          <Grid container spacing={2} px={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                autoComplete="off"
                fullWidth
                name={ENROLLED_LIVES}
                error={!!currentPlanErrors[ENROLLED_LIVES]}
                value={currentPlan[ENROLLED_LIVES] || ""}
                onChange={(e) => {
                  handleNumericFieldChange(
                    e.target,
                    planName,
                    handlePlanFormChange,
                  );
                }}
                onFocus={() => {
                  handlePreviousFieldsValidation(ENROLLED_LIVES);
                }}
                onBlur={(e) => {
                  handlePlanFormChangeWithValidation(e.target);
                }}
                label={
                  <Typography>
                    Number of Enrolled Members{" "}
                    <span className={"required"}>*</span>
                  </Typography>
                }
              />
              {!!currentPlanErrors && (
                <Typography variant="caption" color="danger">
                  {currentPlanErrors[ENROLLED_LIVES]}
                </Typography>
              )}
            </Grid>
          </Grid>
        </>
      )}

      <SectionTitle>Contribution Structure</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl
          onFocus={() => {
            handlePreviousFieldsValidation(CONTRIBUTION_STRUCTURE);
          }}
        >
          <FormLabel id="contribution-structure-label" required>
            Select a Contribution Structure
          </FormLabel>
          <RadioGroup
            value={currentPlan[CONTRIBUTION_STRUCTURE]}
            row
            aria-labelledby="contribution-structure-label"
            name={CONTRIBUTION_STRUCTURE}
            onChange={(e) => {
              handlePlanFormChangeWithValidation(e.target);
            }}
          >
            <FormControlLabel
              value={TOTAL_EMPLOYER_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYER_PAID}
            />
            <FormControlLabel
              value={TOTAL_EMPLOYEE_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYEE_PAID}
            />
            <FormControlLabel
              value={CONTRIBUTORY}
              control={<Radio />}
              label={CONTRIBUTORY}
            />
            <FormControlLabel
              value={GROSS_UP}
              control={<Radio />}
              label={GROSS_UP}
            />
          </RadioGroup>
        </FormControl>
        <Box>
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[CONTRIBUTION_STRUCTURE]}
            </Typography>
          )}
        </Box>
        {currentPlan[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY && (
          <Box mt={2}>
            <FormControl>
              <FormLabel id="contribution-amount-label">
                Add Contribution amount
              </FormLabel>
              <Box mt={1}>
                <RadioGroup
                  value={currentPlan[CONTRIBUTION_TYPE]}
                  aria-labelledby="contribution-amount-label"
                  name={CONTRIBUTION_TYPE}
                  onChange={(e) => {
                    handlePlanFormChange(e.target, planName);
                  }}
                >
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_PERCENTAGE}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_PERCENTAGE}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_PERCENTAGE && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE] || ""}
                            onChange={(e) => {
                              handlePlanFormChange(e.target, planName);
                            }}
                            label={<Typography>% percentage</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_AMOUNT}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_AMOUNT}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_AMOUNT && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE] || ""}
                            onChange={(e) => {
                              handlePlanFormChange(e.target, planName);
                            }}
                            label={<Typography>$ amount</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>
        )}
      </Box>

      <SectionTitle>Rate Type</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl
          onFocus={() => {
            handlePreviousFieldsValidation(RATE_TYPE);
          }}
        >
          <FormLabel id="rate-type-label" required>
            Select a Rate Type
          </FormLabel>
          <RadioGroup
            value={currentPlan[RATE_TYPE]}
            row
            aria-labelledby="rate-type-label"
            name={RATE_TYPE}
            onChange={(e) => {
              handlePlanFormChangeWithValidation(e.target);
            }}
          >
            <FormControlLabel
              value={MONTHLY_PAYROLL}
              control={<Radio />}
              label={MONTHLY_PAYROLL}
            />
            <FormControlLabel
              value={MONTHLY_BENEFITS}
              control={<Radio />}
              label={MONTHLY_BENEFITS}
            />
          </RadioGroup>
        </FormControl>
        <Box>
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[RATE_TYPE]}
            </Typography>
          )}
        </Box>
      </Box>

      <CommissionSection
        handlePlanFormChange={handlePlanFormChange}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        currentPlanErrors={currentPlanErrors}
        currentPlan={currentPlan}
        planName={planName}
        isCurrentCoverage={isCurrentCoverage}
      />

      {!isCurrentCoverage && (
        <BenchmarkSection
          handlePlanFormChange={handlePlanFormChange}
          currentPlan={currentPlan}
          planName={planName}
          link={envVariables.VITE_REACT_APP_LTD_BENCHMARKS_URL || "/"}
          coverageName={coverage?.name}
        />
      )}

      <CommentsField
        currentPlan={currentPlan}
        onChange={(e) => {
          handlePlanFormChange(e.target, planName);
        }}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
      />
    </>
  );

  return (
    <>
      <SectionTitle>Plan Type</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl>
          <FormLabel id="plan-type-label" required>
            Select a Plan Type
          </FormLabel>
          <RadioGroup
            value={coverage.details[PLAN_TYPE]}
            row
            aria-labelledby="plan-type-label"
            name={PLAN_TYPE}
            onChange={handlePlanTypeChange}
          >
            <FormControlLabel
              value={SINGLE_PLAN}
              control={<Radio />}
              label={SINGLE_PLAN}
            />
            <FormControlLabel
              value={BASE_BUY_UP}
              control={<Radio />}
              label={BASE_BUY_UP}
            />
          </RadioGroup>
        </FormControl>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }} mt={5}>
        <Tabs
          variant="fullWidth"
          className="detail-page-tabs"
          value={activeTab}
          onChange={handleChangeTab}
          aria-label="client detail tabs"
        >
          <Tab
            sx={{ maxWidth: "50%" }}
            label={
              <Typography className={isTabError(0) ? "error" : ""}>
                {coverage.details[PLAN_TYPE] === SINGLE_PLAN
                  ? "Single Plan"
                  : "Base/Buy Up Plan 1"}{" "}
                <span className={"required"}>*</span>
              </Typography>
            }
          />
          {coverage.details[PLAN_TYPE] === BASE_BUY_UP && (
            <Tab
              label={
                <Typography className={isTabError(1) ? "error" : ""}>
                  Base/Buy Up Plan 2 <span className={"required"}>*</span>
                </Typography>
              }
            />
          )}
        </Tabs>
      </Box>
      <TabPanel value={activeTab} index={0}>
        {planForm(
          coverage.details[PLAN_TYPE] === SINGLE_PLAN
            ? SINGLE_PLAN
            : BASE_BUY_PLAN_1,
        )}
      </TabPanel>
      {coverage.details[PLAN_TYPE] === BASE_BUY_UP && activeTab === 1 && (
        <TabPanel value={activeTab} index={1}>
          {planForm(BASE_BUY_PLAN_2)}
        </TabPanel>
      )}
    </>
  );
};

export default LongTermDisability;
